import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { Card, CardContent } from '@agion/ui'
import { Server } from 'lucide-react'
import { cn } from '@/core/analytics_utils'
import { queryInstant } from '@/core/observability_api_client'
import type { ServiceStatus, PromQLResult } from '@/core/observability_types'
import { ServiceDetailPanel } from './ServiceDetailPanel'

function parseResultMap(results: PromQLResult[]): Map<string, number> {
  const map = new Map<string, number>()
  for (const r of results) {
    const name = r.metric['service_name'] || r.metric['job'] || ''
    if (name && r.value) {
      map.set(name, parseFloat(r.value[1]) || 0)
    }
  }
  return map
}

export const ServicesPage: React.FC = () => {
  const { t } = useTranslation('pages')
  const [searchParams, setSearchParams] = useSearchParams()
  const [services, setServices] = useState<ServiceStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedService, setSelectedService] = useState<string | null>(
    searchParams.get('service')
  )

  const fetchServices = useCallback(async () => {
    try {
      const [upRes, rateRes, errorRes, p50Res, p95Res, p99Res] = await Promise.all([
        queryInstant('max(up{job=~"analytics.*|auth.*|governance.*|orchestration.*|streaming.*|infra.*"}) by (job)'),
        queryInstant('sum(rate(http_server_request_duration_count[5m])) by (job)'),
        queryInstant('sum(rate(http_server_request_duration_count{http_status_code=~"5.."}[5m])) by (job) / sum(rate(http_server_request_duration_count[5m])) by (job)'),
        queryInstant('histogram_quantile(0.50, sum(rate(http_server_request_duration_bucket[5m])) by (le, job))'),
        queryInstant('histogram_quantile(0.95, sum(rate(http_server_request_duration_bucket[5m])) by (le, job))'),
        queryInstant('histogram_quantile(0.99, sum(rate(http_server_request_duration_bucket[5m])) by (le, job))'),
      ])

      const upResults = upRes.data?.result || []
      const rateMap = parseResultMap(rateRes.data?.result || [])
      const errorMap = parseResultMap(errorRes.data?.result || [])
      const p50Map = parseResultMap(p50Res.data?.result || [])
      const p95Map = parseResultMap(p95Res.data?.result || [])
      const p99Map = parseResultMap(p99Res.data?.result || [])

      const seen = new Set<string>()
      const list: ServiceStatus[] = []

      for (const r of upResults) {
        const name = r.metric['job'] || 'unknown'
        if (seen.has(name)) continue
        seen.add(name)

        const isUp = r.value ? parseFloat(r.value[1]) === 1 : false
        const rate = rateMap.get(name) || 0
        const errRate = errorMap.get(name) || 0

        list.push({
          name,
          status: isUp ? 'up' : 'down',
          requestRate: rate,
          errorRate: isNaN(errRate) ? 0 : errRate,
          latencyP50: ((p50Map.get(name) || 0) * 1000),
          latencyP95: ((p95Map.get(name) || 0) * 1000),
          latencyP99: ((p99Map.get(name) || 0) * 1000),
        })
      }

      // Sort: down services first, then by name
      list.sort((a, b) => {
        if (a.status !== b.status) return a.status === 'down' ? -1 : 1
        return a.name.localeCompare(b.name)
      })

      setServices(list)
    } catch {
      // Keep previous state
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchServices()
    const interval = setInterval(fetchServices, 30000)
    return () => clearInterval(interval)
  }, [fetchServices])

  const handleSelectService = (name: string) => {
    setSelectedService(name)
    setSearchParams({ service: name })
  }

  const handleCloseDetail = () => {
    setSelectedService(null)
    setSearchParams({})
  }

  const fmtLatency = (ms: number) =>
    isNaN(ms) || ms === 0 ? '-' : ms < 1000 ? `${ms.toFixed(0)}ms` : `${(ms / 1000).toFixed(2)}s`

  return (
    <div className="container-standard">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Server className="h-6 w-6 text-slate-600 dark:text-slate-400" />
          <div>
            <h1 className="page-title">{t('observability.servicesTitle', 'Services')}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('observability.servicesDescription', 'Golden signals for each platform service')}
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            {/* eslint-disable-next-line no-restricted-syntax */}
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {t('observability.service', 'Service')}
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {t('observability.status', 'Status')}
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {t('observability.rate', 'Rate')}
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {t('observability.errors', 'Errors')}
                  </th>
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">P50</th>
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">P95</th>
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">P99</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i} className="border-b border-slate-100 dark:border-slate-700/50">
                      <td colSpan={7} className="px-4 py-3">
                        <div className="h-5 animate-pulse bg-slate-100 dark:bg-slate-700 rounded" />
                      </td>
                    </tr>
                  ))
                ) : services.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-400">
                      {t('observability.noServices', 'No services found. LGTM stack may not be receiving metrics yet.')}
                    </td>
                  </tr>
                ) : (
                  services.map(svc => (
                    <tr
                      key={svc.name}
                      onClick={() => handleSelectService(svc.name)}
                      className={cn(
                        'border-b border-slate-100 dark:border-slate-700/50 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors',
                        selectedService === svc.name && 'bg-blue-50 dark:bg-blue-900/20'
                      )}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-slate-100">
                        {svc.name}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn(
                          'inline-flex items-center gap-1.5 text-xs font-medium',
                          svc.status === 'up' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                        )}>
                          <span className={cn(
                            'h-2 w-2 rounded-full',
                            svc.status === 'up' ? 'bg-emerald-500' : 'bg-red-500'
                          )} />
                          {svc.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-slate-700 dark:text-slate-300">
                        {/* eslint-disable-next-line i18next/no-literal-string */}
                        {`${svc.requestRate.toFixed(1)}/s`}
                      </td>
                      <td className={cn(
                        'px-4 py-3 text-sm text-right',
                        svc.errorRate > 0.05 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-slate-700 dark:text-slate-300'
                      )}>
                        {(svc.errorRate * 100).toFixed(2)}%
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-slate-600 dark:text-slate-400">
                        {fmtLatency(svc.latencyP50)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-slate-600 dark:text-slate-400">
                        {fmtLatency(svc.latencyP95)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-slate-600 dark:text-slate-400">
                        {fmtLatency(svc.latencyP99)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {selectedService && (
        <ServiceDetailPanel serviceName={selectedService} onClose={handleCloseDetail} />
      )}
    </div>
  )
}
